"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { Badge } from "@/components/ui/Badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatCurrency } from "@/lib/utils";

type OpportunityKind =
  | "ANNIVERSARY"
  | "BIRTHDAY"
  | "WISHLIST_MATCH"
  | "REPAIR_READY"
  | "LAYAWAY_DUE"
  | "MEMO_DUE"
  | "AGED_INVENTORY";

interface Opportunity {
  id: string;
  kind: OpportunityKind;
  title: string;
  subtitle: string;
  customerId?: string;
  customerName?: string;
  dueDate?: string;
  dueInDays?: number;
  suggestedAction: string;
  estimatedValue?: number;
  confidence?: number;
}

const KIND_META: Record<OpportunityKind, { label: string; color: string; icon: string }> = {
  ANNIVERSARY: { label: "Anniversary", color: "from-rose-300/20 to-rose-500/10 text-rose-200 border-rose-300/30", icon: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" },
  BIRTHDAY: { label: "Birthday", color: "from-amber-200/20 to-amber-400/10 text-amber-200 border-amber-200/30", icon: "M12 8c-1.657 0-3-.895-3-2s1.343-2 3-2 3 .895 3 2-1.343 2-3 2zm0 0c1.657 0 3 .895 3 2s-1.343 2-3 2-3-.895-3-2 1.343-2 3-2zm-9 8a9 9 0 0118 0H3z" },
  WISHLIST_MATCH: { label: "Wishlist match", color: "from-violet-300/20 to-indigo-500/10 text-violet-200 border-violet-300/30", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" },
  REPAIR_READY: { label: "Repair ready", color: "from-emerald-300/20 to-emerald-500/10 text-emerald-200 border-emerald-300/30", icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" },
  LAYAWAY_DUE: { label: "Layaway due", color: "from-sky-300/20 to-sky-500/10 text-sky-200 border-sky-300/30", icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" },
  MEMO_DUE: { label: "Memo due", color: "from-orange-300/20 to-orange-500/10 text-orange-200 border-orange-300/30", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
  AGED_INVENTORY: { label: "Aged inventory", color: "from-zinc-300/20 to-zinc-500/10 text-zinc-200 border-zinc-300/30", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" },
};

const FILTERS: Array<{ value: "ALL" | OpportunityKind; label: string }> = [
  { value: "ALL", label: "All" },
  { value: "ANNIVERSARY", label: "Anniversaries" },
  { value: "BIRTHDAY", label: "Birthdays" },
  { value: "WISHLIST_MATCH", label: "Wishlist matches" },
  { value: "REPAIR_READY", label: "Repairs ready" },
  { value: "LAYAWAY_DUE", label: "Layaways due" },
  { value: "MEMO_DUE", label: "Memos due" },
  { value: "AGED_INVENTORY", label: "Aged inventory" },
];

export default function OpportunitiesPage() {
  const [opps, setOpps] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"ALL" | OpportunityKind>("ALL");

  useEffect(() => {
    fetch("/api/opportunities")
      .then((res) => res.json())
      .then((data) => setOpps(data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (filter === "ALL" ? opps : opps.filter((o) => o.kind === filter)),
    [opps, filter],
  );

  const stats = useMemo(() => {
    const totalValue = opps.reduce((s, o) => s + (o.estimatedValue || 0), 0);
    const urgent = opps.filter((o) => (o.dueInDays ?? 999) <= 7).length;
    return { total: opps.length, urgent, totalValue };
  }, [opps]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-amber-200/30 to-amber-400/10">
              <svg className="h-4 w-4 text-amber-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-white">Opportunities</h1>
            <span className="ml-1 rounded-full border border-amber-200/30 bg-amber-200/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-200/80">
              AI
            </span>
          </div>
          <p className="mt-1 text-sm text-muted">
            Proactive, AI-generated opportunities from your client book and inventory.
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">Total opportunities</p>
          <p className="mt-1 text-2xl font-semibold text-white">{stats.total}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">This week</p>
          <p className="mt-1 text-2xl font-semibold text-amber-200">{stats.urgent}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">Pipeline value</p>
          <p className="mt-1 text-2xl font-semibold text-white">{formatCurrency(stats.totalValue)}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const active = filter === f.value;
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                active
                  ? "border-amber-200/40 bg-amber-200/10 text-amber-200"
                  : "border-white/10 bg-white/5 text-white/60 hover:border-white/20 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No opportunities yet"
          description="Load the jewelry demo data from the dashboard to generate AI-powered opportunities."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => {
            const meta = KIND_META[o.kind];
            return (
              <Card key={o.id} className="transition hover:border-white/20">
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border bg-gradient-to-br ${meta.color}`}>
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d={meta.icon} />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${meta.color}`}>
                          {meta.label}
                        </span>
                        {typeof o.dueInDays === "number" && (
                          <Badge variant={o.dueInDays <= 7 ? "warning" : "default"} size="sm">
                            {o.dueInDays === 0 ? "Today" : o.dueInDays === 1 ? "Tomorrow" : `in ${o.dueInDays} days`}
                          </Badge>
                        )}
                        {typeof o.confidence === "number" && (
                          <span className="text-[10px] text-text-dim">
                            {Math.round(o.confidence * 100)}% confidence
                          </span>
                        )}
                      </div>
                      <h3 className="mt-1.5 text-base font-semibold text-white">{o.title}</h3>
                      <p className="mt-0.5 text-sm text-white/60">{o.subtitle}</p>
                      <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200/20 bg-amber-200/5 p-3">
                        <svg className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-200" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M11 9h2V7h-2v2zm1 11c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h2v-6h-2v6z" />
                        </svg>
                        <p className="text-xs leading-relaxed text-white/80">{o.suggestedAction}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {o.estimatedValue && (
                        <span className="text-sm font-semibold text-white">
                          {formatCurrency(o.estimatedValue)}
                        </span>
                      )}
                      {o.customerId ? (
                        <Link
                          href={`/dashboard/customers/${o.customerId}`}
                          className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white/20"
                        >
                          View client
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
